import { supabase } from '../client';

/** One row per org — Workflows page canvas + saved rules (ORC-010). */
export const WORKFLOW_WORKSPACE_NAME = '__workspace__';

/** ORC-020: persisted execution rows (worker fills these later). */
export interface WorkflowExecutionRow {
  id: string;
  workflow_definition_id: string | null;
  status: string;
  started_at: string;
  completed_at: string | null;
  error: string | null;
  trigger_payload: Record<string, unknown>;
}

export interface WorkflowRule {
  id: string;
  name: string;
  trigger: string;
  action: string;
  created: string;
}

export interface WorkflowCanvasNode {
  id: string;
  type: 'trigger' | 'action';
  label: string;
  x: number;
  y: number;
}

/** §9.15 orchestrator canvas — same JSON shape as `GraphDoc` in `graphModel.ts`. */
export type OrchestratorGraphDoc = {
  nodes: Array<Record<string, unknown>>;
  edges: Array<Record<string, unknown>>;
};

export interface WorkflowWorkspaceGraph {
  savedWorkflows: WorkflowRule[];
  canvasNodes: WorkflowCanvasNode[];
  /** Process Orchestrator canvas (optional). */
  orchestratorGraph?: OrchestratorGraphDoc;
  orchestratorSelectedFlowId?: string;
}

function isOrchestratorGraphDoc(v: unknown): v is OrchestratorGraphDoc {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return Array.isArray(o.nodes) && Array.isArray(o.edges);
}

export const workflowService = {
  /**
   * Returns null if no workspace row exists yet.
   * Empty arrays if row exists with empty graph.
   */
  async getWorkspace(orgId: string): Promise<WorkflowWorkspaceGraph | null> {
    const { data, error } = await supabase
      .from('ggj_b2b_workflow_definitions')
      .select('graph')
      .eq('org_id', orgId)
      .eq('name', WORKFLOW_WORKSPACE_NAME)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    const g = (data.graph ?? {}) as Record<string, unknown>;
    const orchestratorGraph = g.orchestratorGraph;
    return {
      savedWorkflows: Array.isArray(g.savedWorkflows) ? (g.savedWorkflows as WorkflowRule[]) : [],
      canvasNodes: Array.isArray(g.canvasNodes) ? (g.canvasNodes as WorkflowCanvasNode[]) : [],
      ...(isOrchestratorGraphDoc(orchestratorGraph) ? { orchestratorGraph } : {}),
      ...(typeof g.orchestratorSelectedFlowId === 'string'
        ? { orchestratorSelectedFlowId: g.orchestratorSelectedFlowId }
        : {}),
    };
  },

  /**
   * Merges into existing `graph` JSON so Workflows list saves do not wipe orchestrator state,
   * and orchestrator saves do not wipe rules/canvas unless explicitly passed.
   */
  async saveWorkspace(orgId: string, graph: WorkflowWorkspaceGraph): Promise<void> {
    const { data: existing, error: readErr } = await supabase
      .from('ggj_b2b_workflow_definitions')
      .select('graph')
      .eq('org_id', orgId)
      .eq('name', WORKFLOW_WORKSPACE_NAME)
      .maybeSingle();
    if (readErr) throw readErr;

    const prev = (existing?.graph ?? {}) as Record<string, unknown>;
    const nextGraph: Record<string, unknown> = {
      ...prev,
      savedWorkflows: graph.savedWorkflows,
      canvasNodes: graph.canvasNodes,
    };
    if ('orchestratorGraph' in graph && graph.orchestratorGraph !== undefined) {
      nextGraph.orchestratorGraph = graph.orchestratorGraph;
    }
    if ('orchestratorSelectedFlowId' in graph && graph.orchestratorSelectedFlowId !== undefined) {
      nextGraph.orchestratorSelectedFlowId = graph.orchestratorSelectedFlowId;
    }

    const { error } = await supabase.from('ggj_b2b_workflow_definitions').upsert(
      {
        org_id: orgId,
        name: WORKFLOW_WORKSPACE_NAME,
        graph: nextGraph,
        version: 1,
        is_template: false,
      },
      { onConflict: 'org_id,name' }
    );
    if (error) throw error;
  },

  /** §9.15 — update only orchestrator fields; preserves `savedWorkflows` / `canvasNodes` from existing row. */
  async saveOrchestratorCanvas(
    orgId: string,
    canvas: { graph: OrchestratorGraphDoc; selectedFlowId: string }
  ): Promise<void> {
    const { data: existing, error: readErr } = await supabase
      .from('ggj_b2b_workflow_definitions')
      .select('graph')
      .eq('org_id', orgId)
      .eq('name', WORKFLOW_WORKSPACE_NAME)
      .maybeSingle();
    if (readErr) throw readErr;
    const prev = (existing?.graph ?? {}) as Record<string, unknown>;
    const nextGraph: Record<string, unknown> = {
      ...prev,
      orchestratorGraph: canvas.graph,
      orchestratorSelectedFlowId: canvas.selectedFlowId,
    };
    if (!Array.isArray(nextGraph.savedWorkflows)) nextGraph.savedWorkflows = [];
    if (!Array.isArray(nextGraph.canvasNodes)) nextGraph.canvasNodes = [];
    const { error } = await supabase.from('ggj_b2b_workflow_definitions').upsert(
      {
        org_id: orgId,
        name: WORKFLOW_WORKSPACE_NAME,
        graph: nextGraph,
        version: 1,
        is_template: false,
      },
      { onConflict: 'org_id,name' }
    );
    if (error) throw error;
  },

  /**
   * Server-side execution audit rows (migration 018). Rows are inserted by the outbox worker
   * (`scripts/workflow-worker.mjs`), DB triggers, or manual SQL — not by the Process Orchestrator
   * “Run preview” button (that path is browser-only).
   *
   * **RLS:** policy `org_id = current_org_id()` — the Supabase session must carry org context or
   * the client sees zero rows. Optional `orgId` filter further narrows when provided.
   */
  async listRecentExecutions(orgId: string | null, limit = 25): Promise<WorkflowExecutionRow[]> {
    let q = supabase
      .from('ggj_b2b_workflow_executions')
      .select('id, workflow_definition_id, status, started_at, completed_at, error, trigger_payload')
      .order('started_at', { ascending: false })
      .limit(limit);
    if (orgId) q = q.eq('org_id', orgId);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as WorkflowExecutionRow[];
  },
};
